import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Drawer, Box, IconButton, Typography, Chip, Autocomplete, TextField,
  InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Paper, Slide, Badge,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import ChatIcon from '@mui/icons-material/Chat';
import SyncIcon from '@mui/icons-material/Sync';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { CircularProgress } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSelector, useDispatch } from 'react-redux';
import { selectCustomersidname } from '../../../store/customers/customersSlice';
import { SNACKBAR_OPEN } from '../../../store/actions';
import JSDB from 'fromKotlin/nk';
import BulkSendPanel from '../../../ui-component/dialogs/BulkSendDialog';
import DocPickerPanel from './DocPickerPanel';
import { getCofcLink } from '../../../api/customer/documents.api';
import { completeWhatsAppSend } from '../../../hooks/useWhatsAppSendLifecycle';

export interface WhatsAppDrawerProps {
  baseUrl: string;
  token: string;
  sessionId: string;
  openSignal: number;
  onOpenChange?: (open: boolean) => void;
}

type DrawerMode = 'chat' | 'bulk' | 'docs';
type DisplayMode = 'drawer' | 'mini';

const MIN_WIDTH = 380;
const MAX_WIDTH = 900;
const STORAGE_KEY = 'wa-drawer-width';
const DISPLAY_MODE_KEY = 'wa-display-mode';
const MINI_W_KEY = 'wa-mini-width';
const MINI_H_KEY = 'wa-mini-height';

const MINI_DEFAULT_W = 380;
const MINI_DEFAULT_H = 520;
const MINI_MIN_W = 320;
const MINI_MAX_W = 600;
const MINI_MIN_H = 360;
const MINI_MAX_H = 800;

function getStoredWidth(): number {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const n = parseInt(v, 10);
      if (n >= MIN_WIDTH && n <= MAX_WIDTH) return n;
    }
  } catch {}
  return 520;
}

function getStoredDisplayMode(): DisplayMode {
  try {
    const v = localStorage.getItem(DISPLAY_MODE_KEY);
    if (v === 'mini' || v === 'drawer') return v;
  } catch {}
  return 'mini';
}

function getStoredMiniSize(): { w: number; h: number } {
  try {
    const w = parseInt(localStorage.getItem(MINI_W_KEY) || '', 10);
    const h = parseInt(localStorage.getItem(MINI_H_KEY) || '', 10);
    return {
      w: (w >= MINI_MIN_W && w <= MINI_MAX_W) ? w : MINI_DEFAULT_W,
      h: (h >= MINI_MIN_H && h <= MINI_MAX_H) ? h : MINI_DEFAULT_H,
    };
  } catch {}
  return { w: MINI_DEFAULT_W, h: MINI_DEFAULT_H };
}

function cleanPhone(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.startsWith('0') && digits.length === 10) return '972' + digits.slice(1);
  return digits;
}

/** Local calendar YYYY-MM-DD for iframe #date / #link (avoids UTC shift from toISOString). */
function localDateKey(d: Date | undefined): string | undefined {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function coerceActionDate(value: unknown): Date | undefined {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value != null && value !== '') {
    const d = new Date(value as string | number);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return undefined;
}

const WhatsAppDrawer = ({ baseUrl, token, sessionId, openSignal, onOpenChange }: WhatsAppDrawerProps) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<DrawerMode>('chat');
  const [displayMode, setDisplayMode] = useState<DisplayMode>(getStoredDisplayMode);
  const [syncing, setSyncing] = useState(false);
  const [width, setWidth] = useState(getStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [miniSize, setMiniSize] = useState(getStoredMiniSize);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeReady = useRef(false);
  const pendingJid = useRef<string | null>(null);
  const pendingClient = useRef<{
    clientId?: string;
    clientName?: string;
    msg?: string;
    sendCompleteToken?: string;
    actionDateKey?: string;
  }>({});

  /** Action date for cofc link + iframe templates (#date, #link) */
  const actionDateRef = useRef<Date | undefined>(undefined);

  const [bulkClients, setBulkClients] = useState<string[] | undefined>();
  const [bulkMsg, setBulkMsg] = useState<string | undefined>();
  const [bulkDate, setBulkDate] = useState<Date | undefined>();
  const [bulkExternalVars, setBulkExternalVars] = useState<string[] | undefined>();
  /** Set synchronously in wa-open-bulk (before re-render) so BulkSendPanel always gets the token */
  const bulkSendCompleteTokenRef = useRef<string | undefined>(undefined);
  const [bulkRevision, setBulkRevision] = useState(0);

  const [docPickerClient, setDocPickerClient] = useState<{ clientId: string; clientName: string; jid: string; docType: string } | null>(null);

  /** Right-click thread in iframe → pick app client to attach to this JID */
  const [linkClientOpen, setLinkClientOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState<{
    jid: string;
    clientId?: string | null;
    clientName?: string | null;
    displayLabel?: string;
  } | null>(null);
  const [linkPick, setLinkPick] = useState<{ id: string; name: string; label: string } | null>(null);
  const [linkSaving, setLinkSaving] = useState(false);

  /** Send to any WhatsApp number (no CRM client) */
  const [quickSendOpen, setQuickSendOpen] = useState(false);
  const [quickPhone, setQuickPhone] = useState('');
  const [quickMsg, setQuickMsg] = useState('');
  const [quickSending, setQuickSending] = useState(false);

  const customers = useSelector(selectCustomersidname);
  const clientOptions = useMemo(
    () =>
      customers
        .filter((c: any) => c.phone)
        .map((c: any) => ({
          id: c.id,
          label: `${c.name}${c.phone ? ' - ' + c.phone : ''}`,
          name: c.name,
          phone: c.phone,
        })),
    [customers],
  );

  /** All CRM clients (for force-link dialog — may have no phone on file) */
  const linkClientOptions = useMemo(
    () =>
      customers.map((c: any) => ({
        id: String(c.id),
        label: `${c.name}${c.phone ? ' - ' + c.phone : ''} (מס׳ ${c.id})`,
        name: c.name,
        phone: c.phone || '',
      })),
    [customers],
  );

  const iframeSrc = useMemo(() => {
    if (!baseUrl || !sessionId || !token) return '';
    return `${baseUrl}/inbox/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token)}&embed=1`;
  }, [baseUrl, sessionId, token]);

  const sendOpenChat = useCallback((
    jid: string,
    clientId?: string,
    clientName?: string,
    msg?: string,
    sendCompleteToken?: string,
    actionDateKey?: string,
  ) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'openChat',
          jid,
          clientId,
          clientName,
          msg,
          sendCompleteToken,
          ...(actionDateKey ? { actionDateKey } : {}),
        },
        '*',
      );
    }
  }, []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'chatUIReady') {
        iframeReady.current = true;
        if (pendingJid.current) {
          sendOpenChat(
            pendingJid.current,
            pendingClient.current?.clientId,
            pendingClient.current?.clientName,
            pendingClient.current?.msg,
            pendingClient.current?.sendCompleteToken,
            pendingClient.current?.actionDateKey,
          );
          pendingJid.current = null;
          pendingClient.current = {};
        }
      }
      if (e.data?.type === 'requestDocs') {
        const { docType, clientId, clientName, jid } = e.data;
        window.dispatchEvent(new CustomEvent('wa-request-docs', {
          detail: { docType, clientId, clientName, jid },
        }));
      }
      if (e.data?.type === 'requestCofcLink' && e.data.clientId) {
        const cId = Number(e.data.clientId);
        const dateForLink = actionDateRef.current || new Date();
        getCofcLink(cId, dateForLink)
          .then((pass: string) => {
            if (iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                { type: 'cofcLinkResult', pass, clientId: e.data.clientId },
                '*',
              );
            }
          })
          .catch(() => {});
      }
      if (e.data?.type === 'chatMessageSent' && e.source === iframeRef.current?.contentWindow) {
        completeWhatsAppSend(e.data.sendCompleteToken, e.data.success === true, 'iframe');
      }
      if (e.data?.type === 'requestLinkClient' && e.data.jid) {
        setLinkTarget({
          jid: String(e.data.jid),
          clientId: e.data.clientId ?? null,
          clientName: e.data.clientName ?? null,
          displayLabel: e.data.displayLabel ? String(e.data.displayLabel) : undefined,
        });
        setLinkPick(null);
        setLinkClientOpen(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendOpenChat]);

  useEffect(() => {
    if (!open) {
      iframeReady.current = false;
      pendingJid.current = null;
    }
    onOpenChange?.(open);
  }, [open]);

  const openChatForPhone = useCallback((
    phone: string,
    clientId?: string,
    clientName?: string,
    msg?: string,
    sendCompleteToken?: string,
    actionDate?: Date,
  ) => {
    const jid = cleanPhone(phone) + '@s.whatsapp.net';
    actionDateRef.current = coerceActionDate(actionDate);
    const actionDateKey = localDateKey(actionDateRef.current);
    setMode('chat');
    if (!open) setOpen(true);
    if (iframeReady.current) {
      sendOpenChat(jid, clientId, clientName, msg, sendCompleteToken, actionDateKey);
    } else {
      pendingJid.current = jid;
      pendingClient.current = { clientId, clientName, msg, sendCompleteToken, actionDateKey };
    }
  }, [open, sendOpenChat]);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      bulkSendCompleteTokenRef.current = detail?.sendCompleteToken;
      const bulkActionDate = coerceActionDate(detail?.actionDate);
      setBulkClients(detail?.clients || undefined);
      setBulkMsg(detail?.msg || undefined);
      setBulkDate(bulkActionDate);
      setBulkExternalVars(detail?.externalVars || undefined);
      actionDateRef.current = bulkActionDate;
      setBulkRevision((r) => r + 1);
      setMode('bulk');
      setOpen(true);
    };
    window.addEventListener('wa-open-bulk', handler);
    return () => window.removeEventListener('wa-open-bulk', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.phone) {
        openChatForPhone(
          detail.phone,
          detail.clientId,
          detail.clientName,
          detail.msg,
          detail.sendCompleteToken,
          coerceActionDate(detail?.actionDate),
        );
      }
    };
    window.addEventListener('wa-open-chat', handler);
    return () => window.removeEventListener('wa-open-chat', handler);
  }, [openChatForPhone]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.clientId) {
        setDocPickerClient({
          clientId: detail.clientId,
          clientName: detail.clientName || '',
          jid: detail.jid || '',
          docType: detail.docType || 'documents',
        });
        setMode('docs');
      }
    };
    window.addEventListener('wa-request-docs', handler);
    return () => window.removeEventListener('wa-request-docs', handler);
  }, []);

  /** UI cleanup — completeWhatsAppSend runs in BulkSendPanel (token from ref/props). */
  const handleBulkSendComplete = useCallback(() => {
    bulkSendCompleteTokenRef.current = undefined;
    setMode('chat');
  }, []);

  const jidToPhoneLabel = useCallback((jid: string) => {
    const base = jid.split('@')[0] || jid;
    return base.replace(/\D/g, '');
  }, []);

  const handleQuickSend = useCallback(async () => {
    const digits = cleanPhone(quickPhone);
    if (digits.length < 10 || !quickMsg.trim() || !baseUrl || !token || !sessionId) {
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: 'הזן מספר תקין (לפחות 10 ספרות) והודעה',
        variant: 'alert',
        alertSeverity: 'warning',
      });
      return;
    }
    const jid = `${digits}@s.whatsapp.net`;
    setQuickSending(true);
    try {
      const res = await fetch(
        `${baseUrl}/chat/send/${encodeURIComponent(sessionId)}/${encodeURIComponent(jid)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': token },
          body: JSON.stringify({ text: quickMsg.trim(), raw_text: quickMsg.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.message || 'שליחה נכשלה');
      }
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: 'ההודעה נשלחה',
        variant: 'alert',
        alertSeverity: 'success',
      });
      setQuickSendOpen(false);
      setQuickMsg('');
      setQuickPhone('');
      setMode('chat');
      if (!open) setOpen(true);
      openChatForPhone(digits, undefined, undefined, '');
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'refreshThreads' }, '*');
      }
    } catch (e: any) {
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: e?.message || 'שליחה נכשלה',
        variant: 'alert',
        alertSeverity: 'error',
      });
    }
    setQuickSending(false);
  }, [quickPhone, quickMsg, baseUrl, token, sessionId, dispatch, open, openChatForPhone]);

  const handleSaveLinkClient = useCallback(async () => {
    if (!linkTarget?.jid || !linkPick || !baseUrl || !token || !sessionId) return;
    setLinkSaving(true);
    try {
      const res = await fetch(
        `${baseUrl}/chat/link-client/${encodeURIComponent(sessionId)}/${encodeURIComponent(linkTarget.jid)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': token },
          body: JSON.stringify({ client_id: linkPick.id, client_name: linkPick.name }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.message || 'שגיאה בקישור לקוח');
      }
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: `השיחה קושרה ללקוח: ${linkPick.name}`,
        variant: 'alert',
        alertSeverity: 'success',
      });
      setLinkClientOpen(false);
      setLinkTarget(null);
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'refreshThreads' }, '*');
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'openChat',
            jid: linkTarget.jid,
            clientId: String(linkPick.id),
            clientName: linkPick.name,
          },
          '*',
        );
      }
    } catch (err: any) {
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        message: err?.message || 'שגיאה בקישור לקוח',
        variant: 'alert',
        alertSeverity: 'error',
      });
    }
    setLinkSaving(false);
  }, [linkTarget, linkPick, baseUrl, token, sessionId, dispatch]);

  const handleSyncClients = useCallback(async () => {
    if (syncing || !baseUrl || !token || !sessionId) return;
    setSyncing(true);
    try {
      const allClients = customers;
      const batch: { phone: string; client_id: string; client_name: string }[] = [];
      for (const c of allClients) {
        try {
          const client = JSDB().getClient(Number(c.id))?.first;
          if (!client) continue;
          const phones: string[] = [];
          const p1 = client.getPhone?.();
          const p2 = client.getSecondPhone?.();
          const p3 = client.getThirdPhone?.();
          if (p1) phones.push(p1);
          if (p2) phones.push(p2);
          if (p3) phones.push(p3);
          for (const raw of phones) {
            const cleaned = cleanPhone(raw);
            if (cleaned.length >= 10) {
              batch.push({ phone: cleaned, client_id: String(c.id), client_name: c.name || '' });
            }
          }
        } catch {}
      }
      if (!batch.length) {
        dispatch({ type: SNACKBAR_OPEN, open: true, message: 'אין לקוחות עם טלפון תקין', variant: 'alert', alertSeverity: 'warning' });
        setSyncing(false);
        return;
      }
      const CHUNK = 200;
      let totalLinked = 0;
      for (let i = 0; i < batch.length; i += CHUNK) {
        const chunk = batch.slice(i, i + CHUNK);
        try {
          const res = await fetch(
            `${baseUrl}/chat/sync-clients/${encodeURIComponent(sessionId)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-api-key': token },
              body: JSON.stringify({ clients: chunk }),
            },
          );
          const data = await res.json();
          if (data.success) totalLinked += data.data.linked;
        } catch {}
      }
      dispatch({
        type: SNACKBAR_OPEN, open: true,
        message: `סנכרון הושלם: ${totalLinked} לקוחות קושרו מתוך ${batch.length}`,
        variant: 'alert', alertSeverity: 'success',
      });
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'refreshThreads' }, '*');
      }
    } catch {
      dispatch({ type: SNACKBAR_OPEN, open: true, message: 'שגיאה בסנכרון', variant: 'alert', alertSeverity: 'error' });
    }
    setSyncing(false);
  }, [syncing, baseUrl, token, sessionId, customers, dispatch]);

  const switchDisplayMode = useCallback((newMode: DisplayMode) => {
    setDisplayMode(newMode);
    try { localStorage.setItem(DISPLAY_MODE_KEY, newMode); } catch {}
  }, []);

  const handleMiniResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = miniSize.w;
    const startH = miniSize.h;

    const onMove = (ev: MouseEvent) => {
      ev.preventDefault();
      // Bottom+right anchored: drag top-left handle left/up to grow.
      const maxW = Math.min(MINI_MAX_W, Math.max(MINI_MIN_W, window.innerWidth - 24));
      const maxH = Math.min(MINI_MAX_H, Math.max(MINI_MIN_H, window.innerHeight - 24));
      const dw = startX - ev.clientX;
      const dh = startY - ev.clientY;
      setMiniSize({
        w: Math.min(maxW, Math.max(MINI_MIN_W, startW + dw)),
        h: Math.min(maxH, Math.max(MINI_MIN_H, startH + dh)),
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIsDragging(false);
      setMiniSize((s) => {
        try {
          localStorage.setItem(MINI_W_KEY, String(s.w));
          localStorage.setItem(MINI_H_KEY, String(s.h));
        } catch {}
        return s;
      });
    };
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';
    setIsDragging(true);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [miniSize]);

  // Right-anchored drawer: drag the left handle outward (left) to expand.
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const startX = e.clientX;
    const startW = width;

    const onMove = (ev: MouseEvent) => {
      ev.preventDefault();
      // Right-anchored drawer: drag the left handle outward (left) to expand.
      const delta = startX - ev.clientX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + delta)));
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setWidth((w) => { try { localStorage.setItem(STORAGE_KEY, String(w)); } catch {} return w; });
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width]);

  if (!sessionId) return null;

  /* ─────── Shared inner content (used by both drawer and mini) ─────── */
  const innerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minWidth: 0, minHeight: 0 }}>
      {isDragging && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 15,
          cursor: displayMode === 'mini' ? 'nw-resize' : 'col-resize',
        }} />
      )}

      {/* Header */}
      <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 1, backgroundColor: '#00a884', color: '#fff', minHeight: 48,
          minWidth: 0, flexShrink: 0, gap: 0.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
            <WhatsAppIcon fontSize="small" sx={{ flexShrink: 0 }} />
            <Typography variant="subtitle2" fontWeight={600} noWrap>WhatsApp</Typography>
            <Chip label={sessionId} size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', height: 20, fontSize: 10, flexShrink: 1, maxWidth: 120 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="שליחה למספר (ללא לקוח)">
              <IconButton
                size="small"
                onClick={() => { setQuickSendOpen(true); }}
                sx={{ color: '#fff' }}
              >
                <SmsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="סנכרן לקוחות">
              <IconButton size="small" onClick={handleSyncClients} disabled={syncing} sx={{ color: '#fff' }}>
                {syncing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SyncIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {displayMode === 'drawer' ? (
              <Tooltip title="מזער לצ׳אט קטן">
                <IconButton size="small" onClick={() => switchDisplayMode('mini')} sx={{ color: '#fff' }}>
                  <CloseFullscreenIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="הרחב לחלונית מלאה">
                <IconButton size="small" onClick={() => switchDisplayMode('drawer')} sx={{ color: '#fff' }}>
                  <OpenInFullIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Client search + bulk toggle — always visible */}
        <Box sx={{ px: 1, py: 0.75, backgroundColor: '#f0f2f5', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 0, flexShrink: 0 }}>
          <Autocomplete
            size="small" options={clientOptions} sx={{ flex: 1, minWidth: 0 }}
            getOptionLabel={(opt: any) => opt.label}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter((o: any) =>
                o.name.toLowerCase().includes(q) || o.phone.includes(q),
              ).slice(0, 20);
            }}
            onChange={(_, val: any) => {
              if (val?.phone) { setMode('chat'); openChatForPhone(val.phone, String(val.id), val.name); }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="חפש לקוח..." variant="outlined"
                InputProps={{ ...params.InputProps,
                  startAdornment: <InputAdornment position="start"><PersonAddIcon sx={{ fontSize: 18, color: '#667781' }} /></InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: 2, fontSize: 13, '& fieldset': { border: 'none' } } }}
              />
            )}
            noOptionsText="לא נמצאו לקוחות" blurOnSelect clearOnBlur selectOnFocus
          />
          <Tooltip title={mode === 'chat' ? 'שליחה מרובה' : 'שיחות'}>
            <Chip
              icon={mode === 'chat' ? <GroupsIcon sx={{ fontSize: 16 }} /> : <ChatIcon sx={{ fontSize: 16 }} />}
              label={mode === 'chat' ? 'מרובה' : 'שיחות'}
              size="small"
              variant={mode === 'bulk' ? 'filled' : 'outlined'}
              color={mode === 'bulk' ? 'success' : 'default'}
              onClick={() => {
                if (mode === 'chat') {
                  setBulkClients(undefined);
                  setBulkMsg(undefined);
                  setBulkExternalVars(undefined);
                  bulkSendCompleteTokenRef.current = undefined;
                  setBulkRevision((r) => r + 1);
                }
                setMode(mode === 'chat' ? 'bulk' : 'chat');
              }}
              sx={{ cursor: 'pointer', flexShrink: 0 }}
            />
          </Tooltip>
        </Box>

        {/* Content — iframe stays mounted but hidden to avoid reload on mode switch */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: mode === 'chat' ? 'flex' : 'none', minWidth: 0, minHeight: 0 }}>
          {open && iframeSrc && (
            <iframe ref={iframeRef} src={iframeSrc}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block', minWidth: 0, flex: 1 }} title="WhatsApp Chat"
            />
          )}
        </Box>
        {mode === 'bulk' && (
          <BulkSendPanel
            baseUrl={baseUrl} token={token} sessionId={sessionId}
            initialClients={bulkClients} initialMsg={bulkMsg} actionDate={bulkDate}
            externalVars={bulkExternalVars}
            revision={bulkRevision}
            sendCompleteToken={bulkSendCompleteTokenRef.current}
            onBack={() => setMode('chat')}
            onSendComplete={handleBulkSendComplete}
          />
        )}
        {mode === 'docs' && docPickerClient && (
          <DocPickerPanel
            clientId={docPickerClient.clientId}
            clientName={docPickerClient.clientName}
            jid={docPickerClient.jid}
            docType={docPickerClient.docType}
            onBack={() => setMode('chat')}
            onSent={() => setMode('chat')}
          />
        )}

        <Dialog
          open={quickSendOpen}
          onClose={() => !quickSending && (setQuickSendOpen(false))}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>שליחה למספר</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              שליחה לכל מספר וואטסאפ — בלי קישור ללקוח. אפשר לקשר מאוחר יותר בלחיצה ימנית על השיחה.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="מספר טלפון"
              placeholder="0501234567 / 972501234567"
              fullWidth
              value={quickPhone}
              onChange={(e) => setQuickPhone(e.target.value)}
              disabled={quickSending}
              sx={{ mb: 1 }}
              dir="ltr"
              InputProps={{ sx: { textAlign: 'left' } }}
            />
            <TextField
              margin="dense"
              label="הודעה"
              placeholder="טקסט ההודעה…"
              fullWidth
              multiline
              minRows={3}
              value={quickMsg}
              onChange={(e) => setQuickMsg(e.target.value)}
              disabled={quickSending}
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={quickSending} onClick={() => setQuickSendOpen(false)}>ביטול</Button>
            <LoadingButton
              variant="contained"
              loading={quickSending}
              onClick={handleQuickSend}
              disabled={!quickPhone.trim() || !quickMsg.trim()}
            >
              שלח
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={linkClientOpen}
          onClose={() => {
            if (linkSaving) return;
            setLinkClientOpen(false);
            setLinkTarget(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>קישור שיחה ללקוח</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              מספר / שיחה:{' '}
              <strong dir="ltr">{linkTarget ? jidToPhoneLabel(linkTarget.jid) : ''}</strong>
              {linkTarget?.displayLabel ? (
                <span> — {linkTarget.displayLabel}</span>
              ) : null}
            </Typography>
            {linkTarget?.clientId ? (
              <Typography variant="caption" display="block" sx={{ mb: 1.5, color: 'warning.main' }}>
                מקושר כיום ל: {linkTarget.clientName || linkTarget.clientId} — בחירת לקוח חדש תחליף את הקישור.
              </Typography>
            ) : null}
            <Autocomplete
              size="small"
              options={linkClientOptions}
              value={linkPick}
              onChange={(_, v) => setLinkPick(v)}
              getOptionLabel={(opt: any) => opt?.label || ''}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options
                  .filter(
                    (o: any) =>
                      o.name.toLowerCase().includes(q) ||
                      String(o.phone).includes(q) ||
                      String(o.id).includes(q),
                  )
                  .slice(0, 40);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="בחר לקוח מהמערכת"
                  placeholder="חיפוש..."
                  margin="normal"
                  fullWidth
                />
              )}
              noOptionsText="לא נמצאו לקוחות"
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={linkSaving} onClick={() => { setLinkClientOpen(false); setLinkTarget(null); }}>
              ביטול
            </Button>
            <Button
              variant="contained"
              disabled={!linkPick || linkSaving}
              onClick={handleSaveLinkClient}
            >
              {linkSaving ? 'שומר…' : 'קשר ללקוח'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );

  /** Shown when panel is closed — mini + drawer modes (drawer had no button before). */
  const waReopenFab = !open ? (
    <Box
      onClick={() => setOpen(true)}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1300,
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'scale(1.08)', boxShadow: '0 6px 28px rgba(37,211,102,0.5)' },
      }}
    >
      <WhatsAppIcon sx={{ color: '#fff', fontSize: 28 }} />
    </Box>
  ) : null;

  /* ─────── Mini floating panel mode ─────── */
  if (displayMode === 'mini') {
    return (
      <>
        {open && (
          <Paper
            elevation={16}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              width: `min(${miniSize.w}px, calc(100vw - 24px))`,
              height: `min(${miniSize.h}px, calc(100vh - 24px))`,
              zIndex: 1300,
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
              boxShadow: '0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.12)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Resize handle — top-left corner */}
            <Box
              onMouseDown={handleMiniResize}
              sx={{
                position: 'absolute', top: 0, left: 0, width: 18, height: 18,
                cursor: 'nw-resize', zIndex: 20,
                '&::before': {
                  content: '""', position: 'absolute', top: 3, left: 3,
                  width: 8, height: 8,
                  borderTop: '2px solid rgba(255,255,255,0.5)',
                  borderLeft: '2px solid rgba(255,255,255,0.5)',
                },
              }}
            />
            {innerContent}
          </Paper>
        )}

        {waReopenFab}
      </>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        variant="persistent"
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: `${width}px` },
            borderLeft: '1px solid',
            borderColor: 'divider',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
            overflow: 'visible',
            minWidth: 0,
          },
        }}
      >
        {/* Drag handle */}
        <Box
          onMouseDown={handleDragStart}
          sx={{
            position: 'absolute',
            left: -6,
            top: 0,
            bottom: 0,
            width: 12,
            cursor: 'col-resize',
            zIndex: 20,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover .drag-line': { opacity: 1 },
          }}
        >
          <Box
            className="drag-line"
            sx={{
              width: 3,
              height: 48,
              borderRadius: 2,
              backgroundColor: '#00a884',
              opacity: 0.3,
              transition: 'opacity 0.15s',
            }}
          />
        </Box>

        {innerContent}
      </Drawer>
      {waReopenFab}
    </>
  );
};

export default WhatsAppDrawer;
